import { useMemo, useState } from 'react'
import { Badge, Button, Eyebrow, StatusText, StrongText, Subheading, SurfaceCard, SurfaceRow } from '../../../components/ui'
import { EMERGENCY_PROFILE } from '../constants'
import { uiClasses } from '../../shared/uiClasses'

const TEXT_PRIMARY = uiClasses.textPrimary
const TEXT_SECONDARY = uiClasses.textSecondary
const TEXT_SUBTLE = uiClasses.textSubtle
const ICON_SURFACE = uiClasses.iconSurface
const ICON_SURFACE_MUTED = uiClasses.iconSurfaceMuted

interface SharePanelProps {
  onCopySummary: () => Promise<void>
  shareMessage: string | null
  shareTone: 'danger' | 'success'
}

function normalizePhoneForTel(phone: string): string {
  return phone.replace(/[^\d+]/g, '')
}

function MedicalIdCard() {
  return (
    <SurfaceCard className="emergency-medical-id">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${ICON_SURFACE}`}>
            ✚
          </span>
          <Subheading variant="dashboard">Medical ID</Subheading>
        </div>
        <Badge variant="zinc">Emergency</Badge>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-4">
        <SurfaceRow className="flex flex-col items-center px-5 py-3">
          <span className={`text-xs font-medium uppercase tracking-wider ${TEXT_SUBTLE}`}>Blood Type</span>
          <span className={`text-3xl font-extrabold ${TEXT_PRIMARY}`}>{EMERGENCY_PROFILE.bloodType}</span>
        </SurfaceRow>
        {EMERGENCY_PROFILE.organDonor && <Badge variant="zinc">Organ Donor</Badge>}
      </div>

      <div className="mb-5">
        <Eyebrow className="mb-2" variant="section" tone="subtle">Allergies</Eyebrow>
        <div className="flex flex-wrap gap-2">
          {EMERGENCY_PROFILE.allergies.map((allergy) => (
            <Badge key={allergy} variant="zinc">
              <span className="text-[10px]">⚠</span>
              {allergy}
            </Badge>
          ))}
        </div>
      </div>

      <div className="mb-5">
        <Eyebrow className="mb-2" variant="section" tone="subtle">Conditions</Eyebrow>
        <div className="flex flex-wrap gap-2">
          {EMERGENCY_PROFILE.conditions.map((condition) => (
            <Badge key={condition} variant="zinc">
              {condition}
            </Badge>
          ))}
        </div>
      </div>

      <div>
        <Eyebrow className="mb-2" variant="section" tone="subtle">Current Medications</Eyebrow>
        <div className="flex flex-wrap gap-2">
          {EMERGENCY_PROFILE.currentMedications.map((medication) => (
            <Badge key={medication} variant="zinc" size="wide">
              {medication}
            </Badge>
          ))}
        </div>
      </div>
    </SurfaceCard>
  )
}

function SharePanel({ onCopySummary, shareMessage, shareTone }: SharePanelProps) {
  return (
    <SurfaceCard className="emergency-share-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <Subheading variant="dashboard">Share Medical ID</Subheading>
        <Badge variant="zinc">Responder ready</Badge>
      </div>
      <div className="mt-4 flex items-center gap-5">
        <div className={`flex h-28 w-28 shrink-0 items-center justify-center rounded-xl ${ICON_SURFACE_MUTED}`}>
          <span className={`text-xs font-semibold ${TEXT_SUBTLE}`}>QR Code</span>
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-semibold ${TEXT_PRIMARY}`}>Emergency Access</p>
          <div className="mt-3">
            <Button onClick={() => void onCopySummary()}>
              Copy emergency summary
            </Button>
          </div>
          {shareMessage && (
            <StatusText className="mt-2" tone={shareTone === 'danger' ? 'danger' : 'success'} aria-live="polite">
              {shareMessage}
            </StatusText>
          )}
        </div>
      </div>
    </SurfaceCard>
  )
}

function EmergencyContacts() {
  return (
    <section>
      <Subheading variant="dashboard">Emergency contacts</Subheading>
      <div className="emergency-contact-grid mt-3">
        {EMERGENCY_PROFILE.emergencyContacts.map((contact) => (
          <SurfaceRow key={contact.name}>
            <p className={`text-sm font-semibold ${TEXT_PRIMARY}`}>{contact.name}</p>
            <p className={`text-xs ${TEXT_SUBTLE}`}>{contact.relationship}</p>
            <a
              href={`tel:${normalizePhoneForTel(contact.phone)}`}
              className={`mt-1 inline-block text-sm font-medium underline ${TEXT_SECONDARY}`}
            >
              {contact.phone}
            </a>
          </SurfaceRow>
        ))}
      </div>
    </section>
  )
}

function MedicalInformation() {
  const rows = [
    { label: 'Primary Physician', value: EMERGENCY_PROFILE.primaryPhysician },
    { label: 'Insurance Provider', value: EMERGENCY_PROFILE.insuranceProvider },
    { label: 'Member ID', value: EMERGENCY_PROFILE.memberId },
    {
      label: 'Advance Directive',
      value: EMERGENCY_PROFILE.advanceDirective
        ? `On file — updated ${EMERGENCY_PROFILE.advanceDirectiveDate}`
        : 'Not on file',
    },
  ]

  return (
    <section>
      <Subheading variant="dashboard">Medical information</Subheading>
      <div className="mt-3 space-y-2">
        {rows.map(({ label, value }) => (
          <SurfaceRow key={label} className="emergency-medical-row">
            <Eyebrow variant="section" tone="subtle">{label}</Eyebrow>
            <StrongText className="mt-1">{value}</StrongText>
          </SurfaceRow>
        ))}
      </div>
    </section>
  )
}

export default function EmergencyTab() {
  const [shareMessage, setShareMessage] = useState<string | null>(null)
  const [shareTone, setShareTone] = useState<'danger' | 'success'>('success')

  const emergencySummary = useMemo(
    () => [
      `Blood type: ${EMERGENCY_PROFILE.bloodType}`,
      `Organ donor: ${EMERGENCY_PROFILE.organDonor ? 'Yes' : 'No'}`,
      `Allergies: ${EMERGENCY_PROFILE.allergies.join('; ')}`,
      `Conditions: ${EMERGENCY_PROFILE.conditions.join('; ')}`,
      `Current medications: ${EMERGENCY_PROFILE.currentMedications.join('; ')}`,
      `Primary physician: ${EMERGENCY_PROFILE.primaryPhysician}`,
      `Insurance: ${EMERGENCY_PROFILE.insuranceProvider}`,
      `Member ID: ${EMERGENCY_PROFILE.memberId}`,
    ].join('\n'),
    []
  )

  async function handleCopyEmergencySummary() {
    setShareMessage(null)

    try {
      if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
        throw new Error('[CLIPBOARD_UNAVAILABLE] Clipboard API is required to copy emergency summary.')
      }

      await navigator.clipboard.writeText(emergencySummary)
      setShareTone('success')
      setShareMessage('Emergency summary copied to clipboard.')
    } catch (error) {
      setShareTone('danger')
      setShareMessage(error instanceof Error ? error.message : 'Failed to copy emergency summary.')
    }
  }

  return (
    <div className="space-y-6">
      <section className="emergency-overview-layout">
        <MedicalIdCard />
        <SharePanel
          onCopySummary={handleCopyEmergencySummary}
          shareMessage={shareMessage}
          shareTone={shareTone}
        />
      </section>

      <SurfaceCard as="article" className="emergency-details-shell">
        <div className="emergency-details-layout">
          <EmergencyContacts />
          <MedicalInformation />
        </div>
      </SurfaceCard>
    </div>
  )
}
